import { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { ContractStatusOption, useGetContractsByStatusQuery } from '@/graphql/generated';
import textsData from '@/textData/DashboardTexts.json';
import { useUserLanguage, useErrorHandlerContext } from '@/customHooks';
import { theme } from '@/styles';
import { contractStatusMapper } from '@/helpers';
import { LoadingSpinner } from '../atoms';

const COLORS = [
  theme.colors.primary1,
  theme.colors.primary2,
  theme.colors.secondary1,
  theme.colors.secondary2,
];

const CANCELED_COLOR = theme.colors.error;

export const DashboardContractsByStatusCard = () => {
  const userLanguage = useUserLanguage();
  const texts =
    userLanguage === 'es'
      ? textsData.es.contractsByStatusCard
      : textsData.en.contractsByStatusCard;
  const yearSelectorTexts =
    userLanguage === 'es'
      ? textsData.es.yearSelector
      : textsData.en.yearSelector;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);
  const yearOptions = years.map((year) => ({
    name: year.toString(),
    value: year,
  }));

  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const { data, loading, error } = useGetContractsByStatusQuery({
    variables: {
      year: selectedYear,
    },
    fetchPolicy: 'network-only',
  });

  const { open: errorHandle } = useErrorHandlerContext();

  if (error) errorHandle(error);

  const safeData = data?.dashboardData?.contractsByStatus ?? [];

  const countsByStatus = safeData.reduce<Record<ContractStatusOption, number>>(
    (acc, item) => {
      const count = parseInt(item.count ?? '0');
      acc[item.status] =
        (acc[item.status] || 0) + (Number.isNaN(count) ? 0 : count);
      return acc;
    },
    {} as Record<ContractStatusOption, number>,
  );

  const totalContracts = Object.values(countsByStatus).reduce(
    (acc, val) => acc + val,
    0,
  );

  if (loading) {
    return (
      <div className="rounded-xl bg-white p-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
        <div>
          <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <hr className="mt-2 border-0 border-t border-t-[#E5E7EB]" />
        </div>
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!totalContracts) {
    return (
      <div className="rounded-xl bg-white p-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
        <div>
          <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <hr className="mt-2 border-0 border-t border-t-[#E5E7EB]" />
        </div>
        <div className="mb-4 mt-4 flex items-center gap-3">
          <label className="whitespace-nowrap text-base font-medium text-gray-500">
            {yearSelectorTexts?.label || (userLanguage === 'es' ? 'Año' : 'Year')}:
          </label>
          <select
            className="min-w-[100px] cursor-pointer rounded-md border-none bg-[#F8FAFC] px-3 py-2 text-base font-medium text-gray-500 transition hover:bg-[#EEF2F7] focus:outline-none focus:ring-2 focus:ring-[rgba(74,148,98,0.2)]"
            value={selectedYear ?? ''}
            onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{userLanguage === 'es' ? 'Año actual' : 'Current year'}</option>
            {yearOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
        <p className="m-0 text-gray-600">{texts.empty}</p>
      </div>
    );
  }

  const arrangedData = safeData.map((item, index) => ({
    name: contractStatusMapper(item.status, userLanguage),
    value: parseInt(item.count ?? '0'),
    status: item.status,
    color:
      item.status === ContractStatusOption.Canceled
        ? CANCELED_COLOR
        : COLORS[index % COLORS.length],
  }));

  const activeCount =
    (countsByStatus[ContractStatusOption.Booking] ?? 0) +
    (countsByStatus[ContractStatusOption.Initiated] ?? 0);

  const activePercentage =
    totalContracts > 0 ? Math.round((activeCount * 100) / totalContracts) : 0;

  return (
    <div className="rounded-xl bg-white p-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div>
        <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
        <hr className="mt-2 border-0 border-t border-t-[#E5E7EB]" />
      </div>
      <div className="mb-4 mt-4 flex items-center gap-3">
        <label className="whitespace-nowrap text-base font-medium text-gray-500">
          {yearSelectorTexts?.label || (userLanguage === 'es' ? 'Año' : 'Year')}:
        </label>
        <select
          className="min-w-[100px] cursor-pointer rounded-md border-none bg-[#F8FAFC] px-3 py-2 text-base font-medium text-gray-500 transition hover:bg-[#EEF2F7] focus:outline-none focus:ring-2 focus:ring-[rgba(74,148,98,0.2)]"
          value={selectedYear ?? ''}
          onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{userLanguage === 'es' ? 'Año actual' : 'Current year'}</option>
          {yearOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.name}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
        <div className="relative min-h-[200px] w-full">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={arrangedData}
                innerRadius={60}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
              >
                {arrangedData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={arrangedData[index].color}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold">
            {`${activePercentage} %`}
          </div>
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-[18px] text-center text-sm text-[#1F2937]">
            {texts.centerLabel}
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          {arrangedData.map((item) => (
            <div
              key={item.status}
              className="flex items-center gap-3"
            >
              <div className="shrink-0">
                <div
                  className="h-5 w-5 rounded-[25px]"
                  style={{ backgroundColor: item.color }}
                />
              </div>
              <p className="m-0 min-w-0 flex-1 leading-4 text-gray-700">{item.name}</p>
              <p className="m-0 shrink-0 font-bold leading-4 text-gray-900">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
